import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { MultiSelectCombobox } from "../shared/MultiSelectCombobox";
import { toast } from "sonner";
import { Loader2, Paperclip, X } from "lucide-react";

const emptyForm = () => ({
  name: "", tagline: "", description: "", country: "", city: "", website: "",
  type: "", specialty: [], founded: "", employees: "",
  fit_negocio: [], origin: [], event_leads: [], batch: [],
  pipeline: "", status: [], contacts: "", info_adicional: "",
});

// Formulario "+ Nuevo" — 17 campos alineados con Airtable (feedback Ecosistema/
// Partners 2026-08-31 P0 punto 4). Fix delta tarde 31/08: el alta a Dealflow ya
// NO se decide aquí (se hace desde la ficha/drawer con "Pasar a Dealflow"); este
// formulario solo crea el Agente en el Directorio. Se añaden Tagline y Adjuntos.
export function PartnerFormDialog({ open, onOpenChange, catalogs, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => { if (open) { setForm(emptyForm()); setFiles([]); } }, [open]);

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const handleFilesChange = (e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.country.trim() || !form.type || form.specialty.length === 0 ||
        form.fit_negocio.length === 0 || form.origin.length === 0 || !form.pipeline || form.status.length === 0) {
      toast.error("Faltan campos obligatorios (*)");
      return;
    }
    setSaving(true);
    try {
      const created = await api.createPartner(token, form);
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        await api.uploadPartnerAttachment(token, created.id, file);
      }
      toast.success("Agente creado");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error("Error al crear el agente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="partner-form-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo Agente</DialogTitle>
          <DialogDescription>Ficha completa alineada con el catálogo de Airtable — campos con * son obligatorios.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="partner-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Agente *</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="partner-form-type"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>{(catalogs?.type || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Tagline</Label>
            <Input placeholder="Frase corta que resume al agente" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="partner-form-tagline" />
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="partner-form-description" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>País *</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="partner-form-country" />
            </div>
            <div className="space-y-2">
              <Label>Ciudad</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="partner-form-city" />
            </div>
            <div className="space-y-2">
              <Label>Web</Label>
              <Input type="url" placeholder="https://..." value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="partner-form-website" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Especialidad *</Label>
            <MultiSelectCombobox options={catalogs?.specialty || []} value={form.specialty} onChange={(v) => set("specialty", v)} placeholder="Selecciona especialidades..." testId="partner-form-specialty" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fundación (año)</Label>
              <Input type="number" value={form.founded} onChange={(e) => set("founded", e.target.value)} data-testid="partner-form-founded" />
            </div>
            <div className="space-y-2">
              <Label>Empleados</Label>
              <Select value={form.employees} onValueChange={(v) => set("employees", v)}>
                <SelectTrigger data-testid="partner-form-employees"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>{(catalogs?.employees || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fit negocio (UN) *</Label>
            <MultiSelectCombobox options={catalogs?.fit_negocio || []} value={form.fit_negocio} onChange={(v) => set("fit_negocio", v)} placeholder="Selecciona unidades de negocio..." testId="partner-form-fit-negocio" />
          </div>
          <div className="space-y-2">
            <Label>Origen *</Label>
            <MultiSelectCombobox options={catalogs?.origin || []} value={form.origin} onChange={(v) => set("origin", v)} placeholder="Selecciona origen..." testId="partner-form-origin" />
          </div>
          <div className="space-y-2">
            <Label>Event Leads</Label>
            <MultiSelectCombobox options={catalogs?.event_leads || []} value={form.event_leads} onChange={(v) => set("event_leads", v)} placeholder="Selecciona eventos..." testId="partner-form-event-leads" />
          </div>
          <div className="space-y-2">
            <Label>Colección</Label>
            <MultiSelectCombobox options={catalogs?.batch || []} value={form.batch} onChange={(v) => set("batch", v)} placeholder="Selecciona colección..." testId="partner-form-batch" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pipeline *</Label>
              <Select value={form.pipeline} onValueChange={(v) => set("pipeline", v)}>
                <SelectTrigger data-testid="partner-form-pipeline"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>{(catalogs?.pipeline || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status *</Label>
              <MultiSelectCombobox options={catalogs?.status || []} value={form.status} onChange={(v) => set("status", v)} placeholder="Selecciona status..." testId="partner-form-status" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Contactos</Label>
            <Input placeholder="Nombres / emails separados por coma" value={form.contacts} onChange={(e) => set("contacts", e.target.value)} data-testid="partner-form-contacts" />
          </div>
          <div className="space-y-2">
            <Label>Info adicional o comentarios</Label>
            <Textarea rows={2} value={form.info_adicional} onChange={(e) => set("info_adicional", e.target.value)} data-testid="partner-form-info-adicional" />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" />Adjuntos</Label>
            <Input type="file" multiple onChange={handleFilesChange} data-testid="partner-form-attachments-input" />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {files.map((f, i) => (
                  <span key={i} className="text-xs bg-muted rounded-full px-2 py-1 flex items-center gap-1">
                    {f.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => removeFile(i)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="partner-form-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Agente
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
